// frontend/src/components/ImageViewer.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Fade,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DownloadIcon from '@mui/icons-material/Download';

const ImageViewer = ({ 
  open, 
  onClose, 
  images = [], 
  initialIndex = 0,
  title = '圖片檢視器'
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Add ref for the dialog content
  const dialogContentRef = useRef(null);

  // Reset states when dialog opens with new images
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setRotation(0);
      setImageLoading(true);
    }
  }, [open, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          if (document.fullscreenElement) {
            document.exitFullscreen();
            setIsFullscreen(false);
          } else {
            onClose();
          }
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
        case '_':
          handleZoomOut();
          break;
        case 'd':
        case 'D':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleDownload();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex, onClose]);

  // Handle mouse wheel zoom with proper passive event handling
  useEffect(() => {
    const handleWheel = (e) => {
      if (!open) return;
      
      // Prevent default scrolling
      e.preventDefault();
      
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    };

    const contentElement = dialogContentRef.current;
    if (contentElement && open) {
      // Add wheel event listener with passive: false to allow preventDefault
      contentElement.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        contentElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, [open, zoom]); // Include zoom in dependencies

  // Navigation handlers
  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    resetImageState();
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    resetImageState();
  };

  // Reset image state when switching images
  const resetImageState = () => {
    setZoom(1);
    setRotation(0);
    setImageLoading(true);
  };

  // Zoom handlers
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  // Toggle fullscreen
  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Enhanced download handler with CloudFront support
  const handleDownload = async () => {
    const currentImage = images[currentIndex];
    if (!currentImage || !currentImage.url) return;
    
    setIsDownloading(true);
    
    try {
      // Extract filename from URL or use default
      const urlParts = currentImage.url.split('/');
      const defaultFilename = urlParts[urlParts.length - 1].split('?')[0] || `image-${currentIndex + 1}.jpg`;
      const filename = currentImage.name || defaultFilename;
      
      // For CloudFront URLs or same-origin images, use fetch to download
      // This handles CORS better and works with CloudFront signed URLs
      if (currentImage.url.includes('cloudfront.net') || currentImage.url.startsWith('/')) {
        try {
          const response = await fetch(currentImage.url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up blob URL
          window.URL.revokeObjectURL(blobUrl);
        } catch (fetchError) {
          // Fallback to direct download
          const link = document.createElement('a');
          link.href = currentImage.url;
          link.download = filename;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        // For external URLs, use direct download
        const link = document.createElement('a');
        link.href = currentImage.url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      // download error handled silently
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle image load
  const handleImageLoad = (e) => {
    setImageLoading(false);
    setImageDimensions({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight
    });
  };

  if (!images || images.length === 0) return null;

  const currentImage = images[currentIndex] || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          position: 'relative'
        }
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      {/* Top toolbar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          zIndex: 10
        }}
      >
        {/* Left side - Image info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ color: 'white' }}>
            {currentImage.name || `圖片 ${currentIndex + 1}`}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {currentIndex + 1} / {images.length}
          </Typography>
        </Box>

        {/* Right side - Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Main image content - Note: removed onWheel prop */}
      <DialogContent
        ref={dialogContentRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          p: 0,
          overflow: 'hidden',
          height: '100vh',
          width: '100vw'
        }}
      >
        {/* Previous button */}
        {images.length > 1 && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 5,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <ArrowBackIosIcon />
          </IconButton>
        )}

        {/* Image container */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            cursor: zoom > 1 ? 'move' : 'default'
          }}
        >
          {imageLoading && (
            <CircularProgress 
              sx={{ 
                position: 'absolute',
                color: 'white',
                zIndex: 2
              }} 
            />
          )}
          
          <img
            src={currentImage.url || currentImage.preview}
            alt={currentImage.name || `Image ${currentIndex + 1}`}
            onLoad={handleImageLoad}
            onError={() => {
              setImageLoading(false);
            }}
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transition: 'transform 0.3s ease',
              opacity: imageLoading ? 0 : 1,
              userSelect: 'none',
              WebkitUserDrag: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
            draggable={false}
          />
        </Box>

        {/* Next button */}
        {images.length > 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 5,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </DialogContent>

      {/* Bottom toolbar */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 80,
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          zIndex: 10
        }}
      >
        {/* Left side - Zoom and other controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Zoom controls */}
          <IconButton
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            <ZoomOutIcon />
          </IconButton>
          
          <Typography sx={{ color: 'white', minWidth: 60, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          
          <IconButton
            onClick={handleZoomIn}
            disabled={zoom >= 3}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            <ZoomInIcon />
          </IconButton>

          {/* Divider */}
          <Box sx={{ width: 1, height: 30, bgcolor: 'rgba(255, 255, 255, 0.1)', mx: 1 }} />

          {/* Rotate button */}
          <Tooltip title="旋轉圖片">
            <IconButton
              onClick={handleRotate}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <RotateRightIcon />
            </IconButton>
          </Tooltip>

          {/* Fullscreen button */}
          <Tooltip title={isFullscreen ? "退出全螢幕" : "全螢幕"}>
            <IconButton
              onClick={handleFullscreen}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Center - Image info (optional) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {imageDimensions.width > 0 && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {imageDimensions.width} × {imageDimensions.height} px
            </Typography>
          )}
        </Box>

        {/* Right side - Download button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="下載圖片 (Ctrl+D)">
            <IconButton
              onClick={handleDownload}
              disabled={isDownloading}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)'
                },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              {isDownloading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                <DownloadIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Thumbnail strip (optional - for multiple images) */}
      {images.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            p: 1,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            borderRadius: 2,
            maxWidth: '80%',
            overflowX: 'auto',
            zIndex: 10,
            '&::-webkit-scrollbar': {
              height: 6
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: 3
            }
          }}
        >
          {images.map((img, index) => (
            <Box
              key={`thumbnail-${index}`}
              onClick={() => {
                setCurrentIndex(index);
                resetImageState();
              }}
              sx={{
                width: 60,
                height: 60,
                flexShrink: 0,
                border: currentIndex === index ? '2px solid white' : '2px solid transparent',
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                opacity: currentIndex === index ? 1 : 0.6,
                transition: 'all 0.2s',
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              <img
                src={img.url || img.preview}
                alt={`Thumbnail ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Dialog>
  );
};

export default ImageViewer;